import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { BiData } from '@wix/da-bi/pkg/events';
import DSModal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import ReportInappropriate from './_partials/ReportInappropriate';
import ReportHome from './_partials/ReportHome';
import ReportSpamMalwareWarez from './_partials/ReportSpamMalwareWarez';
import ReportSomethingElse from './_partials/ReportSomethingElse';
import ReportIpViolation from './_partials/ReportIpViolation';
import ReportThirdPartyViolation from './_partials/ReportThirdPartyViolation';
import GenericForm from './_partials/GenericForm';
import ReportFooter from './_partials/ReportFooter';
import { ReportSectionForms, ReportSectionTypes } from './redux/constants';
import { getActiveTypeFormData, getActiveTypeFormMap } from './redux/helpers';
import s from './ReportPage.scss';
const ReportPage = (props)=>{
    const { data, deviationId, isError, isInitialized, initialize, submit, submitSuccess, hideDeviation, requestClose } = props;
    const { t } = useTranslation();
    const logBiEvent = useBiLogger();
    const { register, errors, handleSubmit, getValues, setValue } = useForm();
    useEffect(()=>{
        if (!isInitialized && initialize) {
            initialize(deviationId);
        }
    }, [
        isInitialized,
        initialize,
        deviationId
    ]);
    // stores navigated sections to allow Back button to display previously shown section
    const [sectionHistory, setSectionHistory] = useState([
        ReportSectionTypes.Home
    ]);
    const [activeSection] = sectionHistory;
    // selectedSection stores section chosen, waiting for NEXT to be clicked
    const [selectedSection, setSelectedSection] = useState(null);
    // sections with no subsections to navigate to usually have a form to submit
    const [isSubmitVisible, setSubmitVisible] = useState(false);
    // "Submit DMCA" is a custom button which needs to be shown instead of Submit
    const [isDMCAVisible, setDMCAVisible] = useState(false);
    const onCancel = (button)=>{
        logBiEvent(BiData({
            evid: 25,
            button,
            deviationid: deviationId
        }));
        requestClose();
    };
    // fired by sections which have further sections that can be selected to be navigated to
    const onChange = (section, showSubmit)=>{
        setSelectedSection(section);
        logBiEvent(BiData({
            evid: 21,
            type_chosen: section || ReportSectionTypes.Home,
            deviationid: deviationId
        }));
        setSubmitVisible(showSubmit || false);
    };
    // fired by subsections to allow them to determine whether to show "Submit" or "DMCA" instead of "Next"
    const onMount = (formId, section)=>{
        if (formId === ReportSectionForms[ReportSectionTypes.FirstPartyIp]) {
            setDMCAVisible(true);
        } else {
            setSubmitVisible(true);
        }
        if (section) {
            setSelectedSection(section);
        }
    };
    // handle BACK button, show previous section and reset selections
    const onBack = ()=>{
        const [previousSection, ...previousHistory] = sectionHistory;
        if (previousSection) {
            setSectionHistory(previousHistory);
            setSelectedSection(null);
            setSubmitVisible(false);
            setDMCAVisible(false);
            logBiEvent(BiData({
                evid: 26,
                deviationid: deviationId
            }));
        }
    };
    const onSubmit = ()=>{
        if (!data) {
            return;
        }
        let form;
        logBiEvent(BiData({
            evid: 29,
            type_chosen: selectedSection || activeSection || ReportSectionTypes.Home,
            deviationid: deviationId
        }));
        switch(selectedSection){
            // "I just don't want to see this" requires only hiding deviation, no report submitted
            case ReportSectionTypes.Block:
                onHide();
                break;
            // Misplaced requires no form fields, we essentially dev/null the report
            // but we want users to think their report was submitted
            case ReportSectionTypes.Misplaced:
            case ReportSectionTypes.ThirdPartyIp:
                requestClose();
                setTimeout(()=>submitSuccess(deviationId), 300);
                break;
            // all other report types get their form values submitted
            default:
                hideDeviation(deviationId);
                form = getActiveTypeFormData(data, activeSection);
                submit(form.id, deviationId, getValues());
                requestClose();
                break;
        }
    };
    // handle "next" button, navigation to selected section and reset selection
    const onNext = ()=>{
        if (!selectedSection) {
            return;
        }
        logBiEvent(BiData({
            evid: 22,
            report_type: selectedSection,
            deviationid: deviationId
        }));
        setSectionHistory([
            selectedSection,
            ...sectionHistory
        ]);
        setSelectedSection(null);
    };
    const onHide = ()=>{
        hideDeviation(deviationId);
        requestClose();
    };
    // todo: just get the component, don't put a render fn in a fc
    const renderActiveSection = (formDataPayload)=>{
        const formData = getActiveTypeFormData(formDataPayload, activeSection);
        const activeTypeMap = getActiveTypeFormMap(formDataPayload);
        switch(activeSection){
            case ReportSectionTypes.Inappropriate:
                return /*#__PURE__*/ React.createElement(ReportInappropriate, {
                    onChange: onChange,
                    activeTypeMap: activeTypeMap,
                    onHide: onHide
                });
            case ReportSectionTypes.SpamMalwareWarez:
                return /*#__PURE__*/ React.createElement(ReportSpamMalwareWarez, {
                    onChange: onChange,
                    activeTypeMap: activeTypeMap,
                    onHide: onHide
                });
            case ReportSectionTypes.IpViolation:
                return /*#__PURE__*/ React.createElement(ReportIpViolation, {
                    onChange: onChange
                });
            case ReportSectionTypes.SomethingElse:
                return /*#__PURE__*/ React.createElement(ReportSomethingElse, {
                    onChange: onChange,
                    activeTypeMap: activeTypeMap,
                    onHide: onHide
                });
            case ReportSectionTypes.ThirdPartyIp:
                return /*#__PURE__*/ React.createElement(ReportThirdPartyViolation, {
                    onMount: onMount
                });
            case ReportSectionTypes.FirstPartyIp:
            case ReportSectionTypes.Mature:
            case ReportSectionTypes.Underage:
            case ReportSectionTypes.Explicit:
            case ReportSectionTypes.Abuse:
            case ReportSectionTypes.SelfHarm:
            case ReportSectionTypes.Warez:
            case ReportSectionTypes.Malware:
            case ReportSectionTypes.Spam:
            case ReportSectionTypes.MonetizedAi:
                return /*#__PURE__*/ React.createElement(GenericForm, {
                    onMount: onMount,
                    formData: formData,
                    formRegister: register,
                    formErrors: errors,
                    formSetValue: setValue,
                    formGetValues: getValues
                });
            case ReportSectionTypes.Home:
            default:
                return /*#__PURE__*/ React.createElement(ReportHome, {
                    onChange: onChange,
                    data: data
                });
        }
    };
    return /*#__PURE__*/ React.createElement(DSModal, {
        isOpen: true,
        heading: t('moderation.reportDeviation'),
        className: s['root'],
        onRequestClose: ()=>onCancel('x'),
        withScrollableContentWrapper: false
    }, /*#__PURE__*/ React.createElement("section", {
        className: s['content']
    }, !isInitialized || !data ? /*#__PURE__*/ React.createElement(LoadingIndicator, null) : !isError ? renderActiveSection(data) : t('moderation.reportDeviationLoadingError'), /*#__PURE__*/ React.createElement(ReportFooter, {
        onBack: onBack,
        onCancel: ()=>onCancel('cancel'),
        onSubmit: ()=>handleSubmit(onSubmit)(),
        onNext: onNext,
        isBackVisible: sectionHistory.length > 1,
        isCancelVisible: sectionHistory.length === 1,
        isDMCAVisible: isDMCAVisible,
        isNextVisible: !isSubmitVisible && !isDMCAVisible,
        isNextDisabled: selectedSection === null,
        isSubmitVisible: isSubmitVisible,
        deviationId: deviationId
    })));
};
ReportPage.displayName = 'ReportPage';
export default ReportPage;

//# sourceMappingURL=ReportPage.js.map